// Copyright (c) 2024 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * AssetCell component for displaying asset counts with status indicators.
 * Shows the total count and breakdown by status (ok/problem/error).
 */
// No React import needed for TypeScript
import { StatusIndicator } from '@/inventory/components/overview/components/status-indicator';
import { assetCellStyles } from '@/inventory/components/overview/styles';
import { AssetCellProps } from '@/inventory/components/overview/types';
import { formatNumber } from '@/utilities/number';
import { Box, IconButton, Typography } from '@mui/material';

/**
 * AssetCell component
 */
export function AssetCell(props: AssetCellProps) {
  const {
    counts,
    onStatusClick,
    onTotalClick,
    isTotal = false,
    showScrappedAssets = false,
    hideZeroes = false,
  } = props;
  const total =
    counts.FUNCTIONAL +
    counts.NEEDS_REVIEW +
    counts['NON_FUNCTIONAL'] +
    (showScrappedAssets ? counts.SCRAPPED : 0);

  return (
    <Box sx={assetCellStyles.container}>
      {/* Total count */}
      {onTotalClick && total > 0 ? (
        <IconButton
          onClick={onTotalClick}
          size="small"
          sx={{
            px: 1,
            py: 0.5,
            borderRadius: 1,
            minHeight: 'auto',
            minWidth: 'auto',
            height: 'auto',
            transition: 'background-color 0.2s ease',
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.04)',
            },
            '&:focus': {
              outline: '2px solid #1976d2',
              outlineOffset: 1,
            },
          }}
        >
          <Typography
            variant="body2"
            sx={{
              fontFamily: 'monospace',
              fontWeight: isTotal ? 600 : 500,
              fontSize: '0.875rem',
              color: 'text.primary',
            }}
          >
            {formatNumber(total)}
          </Typography>
        </IconButton>
      ) : (
        <Box
          sx={{
            px: 1,
            py: 0.5,
            borderRadius: 1,
            minHeight: 'auto',
            minWidth: 'auto',
            height: 'auto',
            display: 'inline-block',
          }}
        >
          <Typography
            variant="body2"
            sx={{
              fontFamily: 'monospace',
              fontWeight: isTotal ? 600 : 500,
              fontSize: '0.875rem',
              color: 'text.primary',
            }}
          >
            {formatNumber(total)}
          </Typography>
        </Box>
      )}

      {/* Status indicators */}
      <Box sx={assetCellStyles.statusIndicators}>
        <StatusIndicator
          status="FUNCTIONAL"
          count={counts.FUNCTIONAL}
          onClick={
            onStatusClick ? () => onStatusClick('FUNCTIONAL') : undefined
          }
          size="small"
          hideZero={hideZeroes}
        />
        <StatusIndicator
          status="NEEDS_REVIEW"
          count={counts.NEEDS_REVIEW}
          onClick={
            onStatusClick ? () => onStatusClick('NEEDS_REVIEW') : undefined
          }
          size="small"
          hideZero={hideZeroes}
        />
        <StatusIndicator
          status="NON_FUNCTIONAL"
          count={counts['NON_FUNCTIONAL']}
          onClick={
            onStatusClick ? () => onStatusClick('NON_FUNCTIONAL') : undefined
          }
          size="small"
          hideZero={hideZeroes}
        />
        {showScrappedAssets && (
          <StatusIndicator
            status="SCRAPPED"
            count={counts.SCRAPPED}
            onClick={
              onStatusClick ? () => onStatusClick('SCRAPPED') : undefined
            }
            size="small"
            hideZero={hideZeroes}
          />
        )}
      </Box>
    </Box>
  );
}
